import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  ChevronDown,
  ClipboardList,
  LineChart,
  Shield,
  ClipboardCheck,
  Zap,
} from 'lucide-react';
import { LandingGsapEffects } from '../components/LandingGsapEffects';
import { LandingIllustration } from '../components/LandingIllustration';
import { Seo } from '../components/Seo';
import { LANDING_FAQ, WAT_WE_DOEN_LEEK } from '../lib/landingCopy';
import { LANDING_IMAGE_SPECS } from '../lib/landingImageBriefs';
import {
  FLOOR_BAND_MAX_L_M2,
  FLOOR_BAND_MAX_M_M2,
  FLOOR_BAND_MAX_S_M2,
  FLOOR_BAND_MAX_XL_M2,
  PRICING_PARTICULIER_BASIS_INCL_BTW,
  PRICING_PARTICULIER_INCL_BTW,
  formatPriceEur,
  indicativePricingParagraph,
} from '../lib/pricing';

const JOURNEY = [
  {
    step: '1',
    title: 'Scan invullen',
    text: 'Segment, gebouw en kamers — zoals jij het uitlegt. Je voortgang blijft lokaal bewaard tot je verdergaat.',
    Icon: ClipboardList,
  },
  {
    step: '2',
    title: 'Inzicht in kW & jaar',
    text: 'Zie indicatieve warmte- en koellast en jaarperspectief. Optioneel: uurlijks weer en diepere grafieken.',
    Icon: LineChart,
  },
  {
    step: '3',
    title: 'Rapport & delen',
    text: 'Comfort Basis of Plus, printen of opslaan als PDF, en een gratis overzicht om te downloaden — klaar om te delen met collega’s op locatie of met je opdrachtgever.',
    Icon: BarChart3,
  },
] as const;

const TRUST_STRIP = [
  { label: 'Grip op comfort thuis', Icon: Zap },
  { label: 'Per ruimte waar het telt', Icon: Shield },
  { label: 'Thuis én in je project', Icon: CheckCircle2 },
] as const;

const BAND_LABELS = [
  { id: 'S' as const, range: `tot ${FLOOR_BAND_MAX_S_M2} m²` },
  { id: 'M' as const, range: `${FLOOR_BAND_MAX_S_M2 + 1}–${FLOOR_BAND_MAX_M_M2} m²` },
  { id: 'L' as const, range: `${FLOOR_BAND_MAX_M_M2 + 1}–${FLOOR_BAND_MAX_L_M2} m²` },
  { id: 'XL' as const, range: `${FLOOR_BAND_MAX_L_M2 + 1}–${FLOOR_BAND_MAX_XL_M2} m²` },
];

function LandingFaqBlock() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  return (
    <div className="mt-10 grid gap-4 lg:grid-cols-12 lg:gap-6">
      <div className="land-reveal relative overflow-hidden rounded-3xl border border-ct-accent/20 bg-gradient-to-br from-ct-accent/[0.12] via-ct-surface to-ct-elevated/80 p-6 shadow-xl shadow-ct-accent/5 sm:p-8 lg:col-span-5">
        <div
          className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-ct-accent/20 blur-3xl"
          aria-hidden
        />
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-ct-accent">Helder antwoord</p>
        <h3 className="mt-3 text-xl font-bold tracking-tight text-ct-text sm:text-2xl">
          Veelgestelde vragen, zonder omwegen
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-ct-muted">
          Klik een vraag open voor het antwoord. Meer uitleg staat op de uitgebreide FAQ-pagina.
        </p>
        <Link
          to="/faq"
          className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-ct-accent transition hover:gap-3"
        >
          Alle vragen bekijken
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
      <div className="space-y-3 lg:col-span-7">
        {LANDING_FAQ.map((item, i) => {
          const open = openIdx === i;
          return (
            <div
              key={item.q}
              className="land-reveal overflow-hidden rounded-2xl border border-ct-border/90 bg-ct-surface/95 shadow-sm transition-[border-color,box-shadow] duration-300 hover:border-ct-accent/25 hover:shadow-md"
            >
              <button
                type="button"
                aria-expanded={open}
                className="flex w-full items-start gap-3 px-5 py-4 text-left"
                onClick={() => setOpenIdx(open ? null : i)}
              >
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-ct-accent/10 text-ct-accent">
                  <span className="text-xs font-bold tabular-nums">{i + 1}</span>
                </span>
                <span className="min-w-0 flex-1 pt-0.5 text-sm font-semibold leading-snug text-ct-text sm:text-base">
                  {item.q}
                </span>
                <ChevronDown
                  className={`mt-1 h-5 w-5 shrink-0 text-ct-muted transition-transform duration-300 ${
                    open ? 'rotate-180 text-ct-accent' : ''
                  }`}
                  aria-hidden
                />
              </button>
              <div
                className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                  open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                }`}
              >
                <div className="min-h-0 overflow-hidden">
                  <p className="border-t border-ct-border/60 px-5 pb-5 pl-[4.25rem] pr-5 pt-3 text-sm leading-relaxed text-ct-muted">
                    {item.a}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function LandingPage() {
  const spec = LANDING_IMAGE_SPECS;

  return (
    <>
      <Seo
        path="/"
        title="ComfortTool™ — Indicatieve warmte- en koelcheck"
        description="Grip op te warm, te koud of onduidelijk vermogen met ComfortTool™. Online scan, indicatieve kW en jaarperspectief — voor bewoners, installateurs en adviseurs."
        keywords="ComfortTool, warmtelast, koellast, comfort scan, energie, woning, rapport, installateur, adviseur"
      />
      <LandingGsapEffects>
        <div className="mx-auto max-w-6xl px-4 py-10 sm:py-16">
          {/* Hero */}
          <section className="land-reveal grid items-center gap-10 lg:grid-cols-2 lg:gap-12">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-ct-accent">
                ComfortTool™ · Thuis, op kantoor, op locatie
              </p>
              <h1 className="mt-4 text-balance text-3xl font-bold tracking-tight text-ct-text sm:text-4xl lg:text-[2.75rem] lg:leading-[1.1]">
                Grip op comfort — voordat je grote stappen zet
              </h1>
              <p className="mt-5 max-w-xl text-pretty text-base leading-relaxed text-ct-muted sm:text-lg">
                Te koud bij de radiator, te warm boven, of geen idee welk vermogen bij je past? Vul de scan in en zet je
                situatie om in indicatieve cijfers. Zo leg je het uit naar een opdrachtgever, collega op locatie of
                bewoner — zonder eerst door een woud aan opties te moeten.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/scan"
                  className="inline-flex items-center gap-2 rounded-xl bg-ct-accent px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-ct-accent/25 transition hover:bg-blue-500"
                >
                  Start de scan
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
                <Link
                  to="/prijzen"
                  className="inline-flex items-center rounded-xl border border-ct-border bg-ct-surface/80 px-6 py-3.5 text-sm font-semibold text-ct-text backdrop-blur-sm transition hover:border-ct-accent/30 hover:bg-ct-elevated"
                >
                  Bekijk prijzen
                </Link>
                <Link
                  to="/faq"
                  className="inline-flex items-center rounded-xl px-4 py-3.5 text-sm font-semibold text-ct-muted hover:text-ct-accent"
                >
                  FAQ →
                </Link>
              </div>
              <ul className="mt-10 flex flex-wrap gap-4 border-t border-ct-border/80 pt-8">
                {TRUST_STRIP.map(({ label, Icon }) => (
                  <li key={label} className="flex items-center gap-2 text-xs font-medium text-ct-muted sm:text-sm">
                    <Icon className="h-4 w-4 shrink-0 text-ct-accent" aria-hidden />
                    {label}
                  </li>
                ))}
              </ul>
            </div>
            <LandingIllustration
              slot="hero"
              ratio={spec.heroVisual.ratio}
              caption={spec.heroVisual.caption}
              brief={spec.heroVisual.brief}
              className="shadow-2xl shadow-black/20"
            />
          </section>

          {/* Herkenbaar comfortprobleem */}
          <section
            className="land-reveal mt-16 grid items-center gap-10 sm:mt-20 lg:grid-cols-2 lg:gap-12"
            aria-labelledby="comfort-problem-heading"
          >
            <LandingIllustration
              slot="comfortProblem"
              ratio={spec.comfortProblemVisual.ratio}
              caption={spec.comfortProblemVisual.caption}
              brief={spec.comfortProblemVisual.brief}
              className="shadow-xl shadow-black/15 lg:max-w-none"
            />
            <div className="lg:pl-2">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-ct-accent">Herkenbaar thuis</p>
              <h2
                id="comfort-problem-heading"
                className="mt-3 text-balance text-2xl font-bold tracking-tight text-ct-text sm:text-3xl"
              >
                Te koud bij de radiator, te warm in de zomer — of gewoon niet zeker wat je nodig hebt?
              </h2>
              <p className="mt-4 max-w-xl text-pretty text-base leading-relaxed text-ct-muted sm:text-lg">
                ComfortTool™ helpt je om je verhaal helder te krijgen: indicatieve warmte- en koellast en een
                jaarperspectief, per ruimte waar dat zinvol is. Geen vervanging van maatwerk op locatie — wél een stevige
                basis om samen naar oplossingen te werken.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/scan"
                  className="inline-flex items-center gap-2 rounded-xl bg-ct-accent px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-ct-accent/20 transition hover:bg-blue-500"
                >
                  Vul de scan in
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
                <Link
                  to="/help"
                  className="inline-flex items-center rounded-xl border border-ct-border bg-ct-surface/80 px-6 py-3.5 text-sm font-semibold text-ct-text transition hover:border-ct-accent/30 hover:bg-ct-elevated"
                >
                  Hoe het werkt
                </Link>
              </div>
            </div>
          </section>

          {/* Customer journey */}
          <section className="land-reveal mt-20 sm:mt-24" aria-labelledby="journey-heading">
            <div className="mx-auto max-w-2xl text-center">
              <h2 id="journey-heading" className="text-2xl font-bold tracking-tight text-ct-text sm:text-3xl">
                Jouw reis in drie stappen
              </h2>
              <p className="mt-3 text-sm text-ct-muted sm:text-base">
                Dezelfde rustige flow als in de scan — van eerste invoer tot rapport.
              </p>
            </div>
            <ol className="mt-12 grid gap-6 md:grid-cols-3">
              {JOURNEY.map(({ step, title, text, Icon }) => (
                <li
                  key={step}
                  className="land-reveal relative rounded-2xl border border-ct-border bg-ct-surface/90 p-6 pt-8 shadow-sm"
                >
                  <span
                    className="absolute -top-3 left-6 flex h-8 w-8 items-center justify-center rounded-full bg-ct-accent text-xs font-bold text-white shadow-md"
                    aria-hidden
                  >
                    {step}
                  </span>
                  <Icon className="h-8 w-8 text-ct-accent" aria-hidden />
                  <h3 className="mt-4 text-lg font-bold text-ct-text">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ct-muted">{text}</p>
                </li>
              ))}
            </ol>
          </section>

          {/* Feature grid */}
          <section className="land-reveal mt-20 space-y-12 sm:mt-24" aria-labelledby="features-heading">
            <h2 id="features-heading" className="text-center text-2xl font-bold text-ct-text sm:text-3xl">
              Waarom bewoners en professionals ComfortTool™ gebruiken
            </h2>
            <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
              <div>
                <h3 className="text-lg font-bold text-ct-text">Warmte én koeling in één beeld</h3>
                <p className="mt-3 text-sm leading-relaxed text-ct-muted">
                  Niet alleen winter — ook zware zomerdagen horen bij jaar-rond comfort. Zo kijk je verder dan alleen de
                  ketel of warmtepomp.
                </p>
                <ul className="mt-6 space-y-3">
                  {WAT_WE_DOEN_LEEK.map((x) => (
                    <li
                      key={x.slice(0, 48)}
                      className="flex gap-3 rounded-xl border border-ct-border/60 bg-ct-surface/50 px-4 py-3 text-sm leading-relaxed text-ct-muted"
                    >
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-ct-success" aria-hidden />
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <LandingIllustration
                slot="featureThermal"
                ratio={spec.featureThermal.ratio}
                caption={spec.featureThermal.caption}
                brief={spec.featureThermal.brief}
              />
            </div>
            <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
              <LandingIllustration
                slot="featureRooms"
                ratio={spec.featureRooms.ratio}
                caption={spec.featureRooms.caption}
                brief={spec.featureRooms.brief}
                className="lg:order-first"
              />
              <div className="lg:order-last">
                <h3 className="text-lg font-bold text-ct-text">Per ruimte scherp — niet alleen “een totaal”</h3>
                <p className="mt-3 text-sm leading-relaxed text-ct-muted">
                  Zonnige zijkamer, veel glas, of juist een koele noordkant: je ziet waar het zwaarder wordt. Dat helpt bij
                  gesprekken over emitters, ventilatie en zonwering.
                </p>
              </div>
            </div>
            <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
              <div>
                <h3 className="text-lg font-bold text-ct-text">Rapport om mee te nemen naar klant of project</h3>
                <p className="mt-3 text-sm leading-relaxed text-ct-muted">
                  Comfort Basis is strak en vast opgebouwd. Comfort Plus voegt een uitgeschreven leesrapport toe — ideaal om
                  te delen naast de harde cijfers.
                </p>
              </div>
              <LandingIllustration
                slot="featureReport"
                ratio={spec.featureReport.ratio}
                caption={spec.featureReport.caption}
                brief={spec.featureReport.brief}
              />
            </div>
          </section>

          <section className="land-reveal mt-16 rounded-2xl border border-ct-border bg-ct-elevated/40 p-6 sm:p-8">
            <h2 className="text-sm font-bold uppercase tracking-wide text-ct-text">Waar ComfortTool™ bij past</h2>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ct-muted sm:text-base">
              ComfortTool™ is bedoeld om je voor te bereiden: heldere indicaties op basis van je invoer — thuis, bij de
              klant of intern. Voor vergunningen, subsidies of officiële energieprestatie heb je vaak aanvullend maatwerk op
              locatie — daar verwijzen we je liever eerlijk naar toe.
            </p>
          </section>

          {/* Richtprijs — bento */}
          <section
            className="land-reveal relative mt-14 overflow-hidden rounded-3xl border border-ct-border bg-gradient-to-br from-ct-surface via-ct-elevated/30 to-ct-accent/[0.08] p-6 shadow-2xl shadow-black/10 sm:mt-16 sm:p-10"
            aria-labelledby="richtprijs-heading"
          >
            <div
              className="pointer-events-none absolute -left-20 bottom-0 h-64 w-64 rounded-full bg-ct-accent/15 blur-3xl"
              aria-hidden
            />
            <div className="relative">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-ct-accent">Transparant</p>
                  <h2 id="richtprijs-heading" className="mt-2 text-2xl font-bold tracking-tight text-ct-text sm:text-3xl">
                    Richtprijzen op band
                  </h2>
                  <p className="mt-2 max-w-xl text-sm text-ct-muted sm:text-base">
                    Je ziet hieronder wat thuis (incl. btw) ongeveer kost per m²-band. Het definitieve bedrag staat op de
                    betaalpagina vlak voor je betaalt.
                  </p>
                </div>
                <Link
                  to="/prijzen"
                  className="inline-flex items-center gap-2 rounded-xl bg-ct-text px-5 py-3 text-sm font-bold text-ct-bg transition hover:opacity-90 dark:bg-white dark:text-slate-900"
                >
                  Volledig overzicht
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
              </div>
              <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {BAND_LABELS.map(({ id, range }) => (
                  <div
                    key={id}
                    className="rounded-2xl border border-ct-border/80 bg-ct-surface/90 p-4 backdrop-blur-sm transition hover:border-ct-accent/30"
                  >
                    <p className="text-xs font-bold uppercase tracking-wider text-ct-muted">Band {id}</p>
                    <p className="mt-1 text-[11px] text-ct-muted">{range}</p>
                    <p className="mt-3 text-lg font-bold tabular-nums text-ct-text">
                      {formatPriceEur(PRICING_PARTICULIER_INCL_BTW[id])}
                    </p>
                    <p className="text-[11px] text-ct-muted">Comfort Plus incl. btw</p>
                    <p className="mt-2 text-sm font-semibold tabular-nums text-ct-accent">
                      vanaf {formatPriceEur(PRICING_PARTICULIER_BASIS_INCL_BTW[id])}{' '}
                      <span className="font-normal text-ct-muted">Basis</span>
                    </p>
                  </div>
                ))}
              </div>
              <p className="mt-8 text-xs leading-relaxed text-ct-muted sm:text-sm">{indicativePricingParagraph()}</p>
            </div>
          </section>

          {/* FAQ */}
          <section className="land-reveal mt-16 sm:mt-20" aria-labelledby="faq-home-heading">
            <h2 id="faq-home-heading" className="text-xl font-bold text-ct-text sm:text-2xl">
              Veelgestelde vragen
            </h2>
            <LandingFaqBlock />
          </section>

          <section className="land-reveal mt-16 rounded-2xl border border-ct-border bg-ct-elevated/50 p-8 text-center sm:mt-20 sm:p-10">
            <ClipboardCheck className="mx-auto h-9 w-9 text-ct-accent" aria-hidden />
            <p className="mt-4 text-sm font-medium text-ct-muted">Klaar om te beginnen?</p>
            <h2 className="mt-2 text-xl font-bold text-ct-text sm:text-2xl">Start vandaag nog met de scan</h2>
            <Link
              to="/scan"
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-ct-accent px-8 py-3.5 text-sm font-bold text-white shadow-lg shadow-ct-accent/20 hover:bg-blue-500"
            >
              Naar de scan
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
            <p className="mt-6 text-xs text-ct-muted">
              Ook interessant:{' '}
              <Link to="/kennisbank" className="text-ct-accent hover:underline">
                Kennisbank
              </Link>
              {' · '}
              <Link to="/reviews" className="text-ct-accent hover:underline">
                Reviews
              </Link>
              {' · '}
              <Link to="/over-ons" className="text-ct-accent hover:underline">
                Over ons
              </Link>
            </p>
          </section>
        </div>
      </LandingGsapEffects>
    </>
  );
}
